import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { Logo } from "./Logo";
import { UTOPISTAS_LOGO_H } from "./UtopistasLogo";
import { ClownNose, CometaSwoosh } from "./ClownNose";
import { SocialRow } from "./SocialRow";

const nav = [
  { to: "/", label: "Inicio" },
  { to: "/cometa", label: "Cometa" },
  { to: "/podcast", label: "Podcast" },
  { to: "/blog", label: "Blog" },
  { to: "/tejido", label: "Tejido de Utopías" },
  { to: "/constelacion", label: "Constelación" },
  { to: "/marca", label: "Utopistas" },
  { to: "/contacto", label: "Contacto" },
];

const tid = (to) => to.replace(/\//g, "") || "home";

export const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [hover, setHover] = useState(null);
  const location = useLocation();
  const onMarca = location.pathname.startsWith("/marca");

  useEffect(() => { setOpen(false); }, [location.pathname]);
  useEffect(() => { document.body.style.overflow = open ? "hidden" : ""; return () => { document.body.style.overflow = ""; }; }, [open]);

  const isActive = (to) => (to === "/" ? location.pathname === "/" : location.pathname.startsWith(to));

  return (
    <header className={`sticky top-0 z-40 backdrop-blur-md border-b ${onMarca ? "bg-cometa-crema/95 border-cometa-marino/10" : "bg-cometa-crema/85 border-black/5"}`}>
      <div className="max-w-7xl mx-auto px-5 lg:px-10 py-3 flex items-center justify-between">
        {onMarca ? (
          <Link to="/marca" data-testid="nav-logo-link" aria-label="Ir a Utopistas" className="hover:-rotate-2 transition flex items-center gap-3">
            <img src={UTOPISTAS_LOGO_H} alt="Utopistas by Dra. Cometa" className="h-11 md:h-12 w-auto" data-testid="nav-utopistas-logo" />
          </Link>
        ) : (
          <Link to="/" data-testid="nav-logo-link" aria-label="Ir a inicio" className="hover:rotate-[-2deg] transition"><Logo small /></Link>
        )}

        <nav className="hidden lg:flex items-center gap-1 rounded-full bg-cometa-tinta/[0.04] p-1.5" onMouseLeave={() => setHover(null)} data-testid="nav-desktop">
          {nav.map((n) => {
            const act = isActive(n.to); const key = hover ?? (act ? n.to : null);
            return (
              <NavLink key={n.to} to={n.to} end={n.to === "/"} data-testid={`nav-${tid(n.to)}`} onMouseEnter={() => setHover(n.to)} className={`relative px-4 py-2.5 rounded-full text-[0.92rem] font-medium transition-colors duration-200 ${act ? "text-cometa-crema" : "text-cometa-tinta/80 hover:text-cometa-tinta"}`}>
                {key === n.to && (
                  <motion.span layoutId="nav-pill" transition={{ type: "spring", stiffness: 420, damping: 32 }} className={`absolute inset-0 rounded-full ${act ? "bg-cometa-marino" : "bg-cometa-dorado/60"}`} />
                )}
                <span className="relative z-10 flex items-center gap-1.5">{n.label}{act && <ClownNose size={7} />}</span>
              </NavLink>
            );
          })}
        </nav>

        <button onClick={() => setOpen(!open)} className="lg:hidden relative h-12 w-12 rounded-full bg-cometa-tinta text-cometa-crema flex items-center justify-center shadow-lg active:scale-95 transition" aria-label={open ? "Cerrar menú" : "Abrir menú"} aria-expanded={open} data-testid="nav-mobile-toggle">
          <AnimatePresence mode="wait" initial={false}>
            <motion.span key={open ? "x" : "m"} initial={{ rotate: -90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ rotate: 90, opacity: 0 }} transition={{ duration: 0.18 }}>{open ? <X size={22} /> : <Menu size={22} />}</motion.span>
          </AnimatePresence>
        </button>
      </div>

      {createPortal(<AnimatePresence>
        {open && (
          <motion.div initial={{ clipPath: "circle(0% at 92% 6%)" }} animate={{ clipPath: "circle(150% at 92% 6%)" }} exit={{ clipPath: "circle(0% at 92% 6%)" }} transition={{ duration: 0.5, ease: [0.76, 0, 0.24, 1] }} className="lg:hidden fixed inset-0 top-[72px] z-[35] bg-cometa-tinta text-cometa-crema overflow-y-auto" data-testid="nav-mobile-menu">
            <CometaSwoosh className="absolute inset-0 w-full h-full" color="#45B2FD" opacity={0.18} />
            <div className="relative px-7 pt-10 pb-24 flex flex-col min-h-full">
              <motion.p initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.1 }} className="font-script text-4xl text-cometa-dorado mb-4">¿A dónde vamos?</motion.p>
              <nav className="flex flex-col">
                {nav.map((n, i) => (
                  <motion.div key={n.to} initial={{ opacity: 0, y: 28, rotateX: -35 }} animate={{ opacity: 1, y: 0, rotateX: 0 }} transition={{ delay: 0.12 + i * 0.06, duration: 0.5, ease: [0.22, 1, 0.36, 1] }} style={{ transformOrigin: "left center" }}>
                    <NavLink to={n.to} end={n.to === "/"} onClick={() => setOpen(false)} data-testid={`nav-mobile-${tid(n.to)}`} className={({ isActive }) => `group relative flex items-center gap-4 py-3.5 font-display text-[2rem] leading-none active:text-cometa-dorado transition-colors ${isActive ? "text-cometa-dorado" : "text-cometa-crema"}`}>
                      {({ isActive }) => (<>
                        <span className={`h-2.5 w-2.5 rounded-full transition-all duration-300 ${isActive ? "bg-cometa-carmesi scale-125 shadow-[0_0_14px_#E01F26]" : "bg-cometa-crema/25 group-active:bg-cometa-dorado"}`} />
                        <span className="relative">
                          {n.label}
                          <span className={`absolute left-0 -bottom-1 h-[2px] bg-cometa-dorado transition-all duration-300 ${isActive ? "w-full" : "w-0 group-active:w-full"}`} />
                        </span>
                        <ArrowUpRight size={22} className="ml-auto opacity-30 group-active:opacity-100 group-active:rotate-45 transition" />
                      </>)}
                    </NavLink>
                  </motion.div>
                ))}
              </nav>
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.6 }} className="mt-auto pt-10">
                <SocialRow />
              </motion.div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>, document.body)}
    </header>
  );
};
